import { useMemo, useState } from 'react'
import type { Key } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Popconfirm,
  Row,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  ClearOutlined,
  EyeOutlined,
  LinkOutlined,
  ReloadOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import type { AdminRecordAction } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'
import { DebouncedSearchInput } from '@/components/DebouncedSearchInput'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'

export const Route = createFileRoute('/_authed/cross-media')({
  component: CrossMediaPage,
})

const PAGE_SIZE = 50
const NUM_STYLE = { fontVariantNumeric: 'tabular-nums' } as const

type LinkStatus = 'pending' | 'verified' | 'rejected'
type LinkStatusOrOrphan = LinkStatus | 'orphan'
type StatusFilter = 'all' | LinkStatusOrOrphan
type RiskFilter =
  | 'all'
  | 'multi'
  | 'same_source'
  | 'pending'
  | 'rejected'
  | 'orphan'
  | 'high'
  | 'medium'
  | 'low'

interface CrossMediaLink {
  anime_id: number
  anime_title: string
  anime_bangumi_id: number | null
  comic_id: number
  comic_title: string | null
  comic_slug: string | null
  comic_cover_url: string | null
  latest_chapter_title: string | null
  latest_chapter_updated_at: string | null
  comic_is_finished: boolean | null
  relation_type: string
  source_work_bangumi_id: number | null
  confidence: number | null
  match_method: string
  status: LinkStatusOrOrphan
  evidence: Record<string, unknown> | null
  created_at: string | null
  updated_at: string | null
  verified_at: string | null
  synced_at: string | null
  in_public_projection: boolean
  is_orphan: boolean
}

interface CrossMediaGroup {
  anime_id: number
  anime_title: string
  anime_bangumi_id: number | null
  link_count: number
  direct_count: number
  same_source_count: number
  verified_count: number
  pending_count: number
  rejected_count: number
  orphan_count: number
  public_count: number
  risk_level: 'high' | 'medium' | 'low'
  latest_at: string | null
  links: CrossMediaLink[]
}

interface CrossMediaListResult {
  ok: boolean
  total?: number
  counts?: { status?: Partial<Record<LinkStatusOrOrphan, number>> }
  rows?: CrossMediaGroup[]
  error?: string
}

interface CrossMediaStats {
  ok: boolean
  total_links?: number
  public_projection?: number
  multi_mapped_anime?: number
  same_source_links?: number
  pending_links?: number
  rejected_links?: number
  orphan_projection_count?: number
  error?: string
}

const STATUS_LABELS: Record<LinkStatusOrOrphan, string> = {
  verified: '已验证',
  pending: '待审核',
  rejected: '已拒绝',
  orphan: '孤儿投影',
}

const STATUS_COLORS: Record<LinkStatusOrOrphan, string> = {
  verified: 'success',
  pending: 'warning',
  rejected: 'error',
  orphan: 'magenta',
}

const RELATION_LABELS: Record<string, string> = {
  direct_adaptation: '直接改编',
  same_source: '同源作品',
  shared_ip: '同 IP',
  sequel_related: '续作关联',
  spinoff: '衍生',
  related: '相关',
}

const RISK_LABELS: Record<CrossMediaGroup['risk_level'], string> = {
  high: '高风险',
  medium: '中风险',
  low: '低风险',
}

const RISK_COLORS: Record<CrossMediaGroup['risk_level'], string> = {
  high: 'error',
  medium: 'warning',
  low: 'default',
}

const STATUS_OPTIONS: Array<{ label: string; value: StatusFilter }> = [
  { label: '全部', value: 'all' },
  { label: '已验证', value: 'verified' },
  { label: '待审核', value: 'pending' },
  { label: '已拒绝', value: 'rejected' },
  { label: '孤儿投影', value: 'orphan' },
]

const RISK_OPTIONS: Array<{ label: string; value: RiskFilter }> = [
  { label: '多映射', value: 'multi' },
  { label: '同源', value: 'same_source' },
  { label: '孤儿', value: 'orphan' },
  { label: '待审', value: 'pending' },
  { label: '高风险', value: 'high' },
  { label: '全部', value: 'all' },
]

const RPC_ERROR_LABELS: Record<string, string> = {
  forbidden: '没有管理员权限',
  invalid_status: '状态不合法',
  invalid_relation_type: '关联类型不合法',
  invalid_risk: '风险筛选不合法',
  invalid_target: '目标关联不完整',
  invalid_links: '批量目标格式不正确',
  too_many_links: '单次最多处理 500 条关联',
  not_found: '关联不存在或已被删除',
  projection_missing: '缺少前端投影快照，请重新运行 materialize 后再恢复',
}

function rpcErrorText(error: Error): string {
  return RPC_ERROR_LABELS[error.message] ?? error.message
}

function isRpcMissingError(error: Error): boolean {
  const text =
    `${error.message} ${(error as { details?: string }).details ?? ''} ${(error as { hint?: string }).hint ?? ''}`.toLowerCase()
  return (
    text.includes('could not find the function') ||
    text.includes('rpc not found') ||
    text.includes('schema cache') ||
    text.includes('pgrst202')
  )
}

function crossMediaErrorDescription(error: Error) {
  if (!isRpcMissingError(error)) return rpcErrorText(error)

  return (
    <Space direction="vertical" size={4}>
      <Typography.Text>
        后台 RPC 还没有部署到当前 Supabase 项目，或 PostgREST schema cache
        尚未刷新。
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        应用迁移：20260614141412_admin_cross_media_manager.sql
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {
          '部署命令：node supabase/scripts/cross_media_admin_deploy.mjs --linked --apply'
        }
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {
          '验证命令：node supabase/scripts/cross_media_admin_smoke.mjs --presence-only'
        }
      </Typography.Text>
    </Space>
  )
}

function relationLabel(value: string): string {
  return RELATION_LABELS[value] ?? value
}

function linkKey(link: CrossMediaLink): string {
  return `${link.anime_id}:${link.comic_id}`
}

function confidenceText(value: number | null): string {
  if (value == null) return '-'
  return `${Math.round(value * 100)}%`
}

function compactCount(value: number | null | undefined): number {
  return value ?? 0
}

function targetLinks(groups: CrossMediaGroup[]): Array<{
  anime_id: number
  comic_id: number
}> {
  return groups.flatMap((group) =>
    group.links
      .filter((link) => link.status !== 'orphan')
      .map((link) => ({ anime_id: link.anime_id, comic_id: link.comic_id })),
  )
}

function bulkPreviewLinks(groups: CrossMediaGroup[]): CrossMediaLink[] {
  return groups.flatMap((group) =>
    group.links.filter((link) => link.status !== 'orphan'),
  )
}

function orphanPreviewLinks(groups: CrossMediaGroup[]): CrossMediaLink[] {
  return groups.flatMap((group) =>
    group.links.filter((link) => link.status === 'orphan'),
  )
}

function previewList(
  links: CrossMediaLink[],
  overflowCount: number,
): React.ReactNode {
  return (
    <div
      style={{
        maxHeight: 220,
        overflow: 'auto',
        border: '1px solid var(--ant-color-border-secondary)',
        borderRadius: 6,
        padding: 8,
        background: 'var(--ant-color-fill-quaternary)',
      }}
    >
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        {links.map((link) => (
          <div key={linkKey(link)} style={{ fontSize: 12 }}>
            <Typography.Text strong>{link.anime_title}</Typography.Text>
            <Typography.Text type="secondary"> / </Typography.Text>
            <Typography.Text>
              {link.comic_title ?? `漫画 #${link.comic_id}`}
            </Typography.Text>
            <Typography.Text type="secondary">
              {' '}
              #{link.anime_id}:{link.comic_id}
            </Typography.Text>
          </div>
        ))}
        {overflowCount > 0 ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            还有 {overflowCount} 条未显示
          </Typography.Text>
        ) : null}
      </Space>
    </div>
  )
}

function statusTag(status: LinkStatusOrOrphan) {
  return <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
}

function riskTag(risk: CrossMediaGroup['risk_level']) {
  return <Tag color={RISK_COLORS[risk]}>{RISK_LABELS[risk]}</Tag>
}

function CrossMediaPage() {
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const [status, setStatus] = useState<StatusFilter>('all')
  const [risk, setRisk] = useState<RiskFilter>('multi')
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([])
  const [detail, setDetail] = useState<CrossMediaGroup | null>(null)
  const isMobile = useIsMobile()

  const statsQuery = useQuery({
    queryKey: ['admin_cross_media_stats'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_cross_media_stats')
      if (error) throw error
      const result = data as CrossMediaStats
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return result
    },
  })

  const listQuery = useQuery({
    queryKey: ['admin_cross_media_links', { status, risk, keyword, page }],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_cross_media_links', {
          p_status: status === 'all' ? null : status,
          p_relation_type: null,
          p_risk: risk === 'all' ? null : risk,
          p_search: keyword || null,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const result = data as CrossMediaListResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return {
        rows: result.rows ?? [],
        total: result.total ?? 0,
        counts: result.counts ?? {},
      }
    },
  })

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0
  const selectedGroups = useMemo(
    () => rows.filter((row) => selectedKeys.includes(row.anime_id)),
    [rows, selectedKeys],
  )

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin_cross_media_links'] })
    void qc.invalidateQueries({ queryKey: ['admin_cross_media_stats'] })
  }

  const setStatusMutation = useMutation({
    mutationFn: async (vars: {
      animeId: number
      comicId: number
      nextStatus: LinkStatus
      note?: string
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('set_cross_media_link_status', {
          p_anime_id: vars.animeId,
          p_comic_id: vars.comicId,
          p_status: vars.nextStatus,
          p_note: vars.note ?? null,
        })
      if (error) throw error
      if (!data.ok) throw new Error(data.error ?? 'unknown')
      return data
    },
    onSuccess: (_data, vars) => {
      message.success(`已改为 ${STATUS_LABELS[vars.nextStatus]}`)
      invalidate()
    },
    onError: (error: Error) =>
      message.error(`状态更新失败：${rpcErrorText(error)}`),
  })

  const bulkStatusMutation = useMutation({
    mutationFn: async (vars: {
      links: Array<{ anime_id: number; comic_id: number }>
      nextStatus: LinkStatus
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('bulk_set_cross_media_link_status', {
          p_links: vars.links,
          p_status: vars.nextStatus,
          p_note: 'admin_cross_media_bulk_action',
        })
      if (error) throw error
      if (!data.ok) throw new Error(data.error ?? 'unknown')
      return data as { updated?: unknown[]; errors?: unknown[] }
    },
    onSuccess: (data, vars) => {
      setSelectedKeys([])
      message.success(
        `批量改为 ${STATUS_LABELS[vars.nextStatus]}：${data.updated?.length ?? 0} 条成功，${data.errors?.length ?? 0} 条失败`,
      )
      invalidate()
    },
    onError: (error: Error) =>
      message.error(`批量操作失败：${rpcErrorText(error)}`),
  })

  const cleanupMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('cleanup_cross_media_orphans', {
          p_note: 'admin_cross_media_cleanup_orphans',
        })
      if (error) throw error
      if (!data.ok) throw new Error(data.error ?? 'unknown')
      return data as { deleted_count?: number }
    },
    onSuccess: (data) => {
      message.success(`已清理 ${data.deleted_count ?? 0} 条孤儿投影`)
      invalidate()
    },
    onError: (error: Error) =>
      message.error(`清理失败：${rpcErrorText(error)}`),
  })

  async function confirmCleanupOrphans() {
    const closeLoading = message.loading('正在计算清理范围...', 0)
    try {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_cross_media_links', {
          p_status: 'orphan',
          p_relation_type: null,
          p_risk: 'orphan',
          p_search: null,
          p_limit: 12,
          p_offset: 0,
        })
      closeLoading()
      if (error) throw error
      const result = data as CrossMediaListResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')

      const previewLinks = orphanPreviewLinks(result.rows ?? [])
      const totalOrphans =
        result.total ?? statsQuery.data?.orphan_projection_count ?? 0
      modal.confirm({
        title: '清理孤儿投影？',
        content: (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text>
              将删除 {totalOrphans} 条没有 verified
              内部关联支撑的前端投影；不会删除 media_links 内部关联。
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              预计清理条目
            </Typography.Text>
            {previewLinks.length > 0 ? (
              previewList(
                previewLinks,
                Math.max(0, totalOrphans - previewLinks.length),
              )
            ) : (
              <Typography.Text type="secondary">
                当前没有可清理的孤儿投影。
              </Typography.Text>
            )}
          </Space>
        ),
        okText: '确认清理',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: () => cleanupMutation.mutate(),
      })
    } catch (error) {
      closeLoading()
      message.error(`预览失败：${rpcErrorText(error as Error)}`)
    }
  }

  function confirmBulk(nextStatus: LinkStatus) {
    const links = targetLinks(selectedGroups)
    const previewLinks = bulkPreviewLinks(selectedGroups)
    if (links.length === 0) {
      message.warning('请选择至少一条有内部关联的记录')
      return
    }
    modal.confirm({
      title: `批量改为 ${STATUS_LABELS[nextStatus]}？`,
      content: (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text>
            将影响 {links.length} 条漫画-动画关联；pending/rejected
            会同时移除前端投影。
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            预计影响条目
          </Typography.Text>
          {previewList(
            previewLinks.slice(0, 12),
            Math.max(0, previewLinks.length - 12),
          )}
        </Space>
      ),
      okText: '确认',
      cancelText: '取消',
      okButtonProps: { danger: nextStatus === 'rejected' },
      onOk: () => bulkStatusMutation.mutate({ links, nextStatus }),
    })
  }

  const columns = useMemo<ColumnsType<CrossMediaGroup>>(
    () => [
      {
        title: '动画',
        key: 'anime',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Link
              to="/anime/$animeId/episodes"
              params={{ animeId: String(row.anime_id) }}
            >
              {row.anime_title}
            </Link>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{row.anime_id}
              {row.anime_bangumi_id ? ` · bgm ${row.anime_bangumi_id}` : ''}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '风险',
        dataIndex: 'risk_level',
        render: (value: CrossMediaGroup['risk_level'], row) => (
          <Space size={4}>
            {riskTag(value)}
            {row.orphan_count > 0 ? (
              <Tooltip title="前端投影没有 verified 内部关联支撑">
                <WarningOutlined style={{ color: 'var(--ant-color-error)' }} />
              </Tooltip>
            ) : null}
          </Space>
        ),
      },
      {
        title: '关联概况',
        key: 'counts',
        render: (_, row) => (
          <Space size={4} wrap>
            <Tag>总 {row.link_count}</Tag>
            <Tag color="blue">直接 {row.direct_count}</Tag>
            <Tag color="purple">同源 {row.same_source_count}</Tag>
            <Tag color="green">前端 {row.public_count}</Tag>
          </Space>
        ),
      },
      {
        title: '状态',
        key: 'status',
        render: (_, row) => (
          <Space size={4} wrap>
            {row.verified_count > 0 ? (
              <Tag color="success">{row.verified_count} 已验证</Tag>
            ) : null}
            {row.pending_count > 0 ? (
              <Tag color="warning">{row.pending_count} 待审</Tag>
            ) : null}
            {row.rejected_count > 0 ? (
              <Tag color="error">{row.rejected_count} 拒绝</Tag>
            ) : null}
            {row.orphan_count > 0 ? (
              <Tag color="magenta">{row.orphan_count} 孤儿</Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: '更新时间',
        dataIndex: 'latest_at',
        render: (value: string | null) => (
          <span style={NUM_STYLE}>{formatCST(value, 'MM-DD HH:mm')}</span>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        render: (_, row) => (
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetail(row)}
          >
            查看
          </Button>
        ),
      },
    ],
    [],
  )

  const linkColumns = useMemo<ColumnsType<CrossMediaLink>>(
    () => [
      {
        title: '漫画',
        key: 'comic',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>
              {row.comic_title ?? `漫画 #${row.comic_id}`}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{row.comic_id}
              {row.comic_slug ? ` · slug ${row.comic_slug}` : ''}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '关系',
        key: 'relation',
        render: (_, row) => (
          <Space size={4} wrap>
            <Tag
              color={
                row.relation_type === 'direct_adaptation' ? 'blue' : 'purple'
              }
            >
              {relationLabel(row.relation_type)}
            </Tag>
            {row.source_work_bangumi_id ? (
              <Tag>source {row.source_work_bangumi_id}</Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: '状态',
        dataIndex: 'status',
        render: (value: LinkStatusOrOrphan, row) => (
          <Space size={4}>
            {statusTag(value)}
            {row.in_public_projection ? (
              <Badge status="success" text="前端显示" />
            ) : null}
          </Space>
        ),
      },
      {
        title: '置信',
        dataIndex: 'confidence',
        align: 'right',
        render: (value: number | null) => (
          <span style={NUM_STYLE}>{confidenceText(value)}</span>
        ),
      },
      {
        title: '方法',
        dataIndex: 'match_method',
        render: (value: string) => <Tag>{value}</Tag>,
      },
      {
        title: '操作',
        key: 'actions',
        render: (_, row) =>
          row.status === 'orphan' ? (
            <Typography.Text type="secondary">清理孤儿投影</Typography.Text>
          ) : (
            <Space size={4}>
              {row.status !== 'verified' ? (
                <Button
                  size="small"
                  type="text"
                  icon={<CheckCircleOutlined />}
                  disabled={setStatusMutation.isPending}
                  onClick={() =>
                    setStatusMutation.mutate({
                      animeId: row.anime_id,
                      comicId: row.comic_id,
                      nextStatus: 'verified',
                      note: 'admin_cross_media_verify',
                    })
                  }
                >
                  验证
                </Button>
              ) : null}
              {row.status !== 'pending' ? (
                <Button
                  size="small"
                  type="text"
                  disabled={setStatusMutation.isPending}
                  onClick={() =>
                    setStatusMutation.mutate({
                      animeId: row.anime_id,
                      comicId: row.comic_id,
                      nextStatus: 'pending',
                      note: 'admin_cross_media_pending',
                    })
                  }
                >
                  待审
                </Button>
              ) : null}
              {row.status !== 'rejected' ? (
                <Popconfirm
                  title="拒绝这条关联？"
                  description="会同时移除前端投影，并保留投影快照用于恢复。"
                  okButtonProps={{ danger: true }}
                  onConfirm={() =>
                    setStatusMutation.mutate({
                      animeId: row.anime_id,
                      comicId: row.comic_id,
                      nextStatus: 'rejected',
                      note: 'admin_cross_media_reject',
                    })
                  }
                >
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<StopOutlined />}
                    disabled={setStatusMutation.isPending}
                  >
                    拒绝
                  </Button>
                </Popconfirm>
              ) : null}
            </Space>
          ),
      },
    ],
    [setStatusMutation],
  )

  /* 抽屉内单条关联的状态动作（手机卡片用；与桌面 linkColumns 的操作列一致） */
  function linkActions(row: CrossMediaLink): AdminRecordAction[] {
    const mutate = (nextStatus: LinkStatus, note: string) =>
      setStatusMutation.mutate({
        animeId: row.anime_id,
        comicId: row.comic_id,
        nextStatus,
        note,
      })
    return [
      {
        key: 'pending',
        label: '待审',
        visible: row.status !== 'pending',
        disabled: setStatusMutation.isPending,
        onClick: () => mutate('pending', 'admin_cross_media_pending'),
      },
      {
        key: 'reject',
        label: '拒绝',
        icon: <StopOutlined />,
        danger: true,
        visible: row.status !== 'rejected',
        disabled: setStatusMutation.isPending,
        confirm: {
          title: '拒绝这条关联？',
          description: '会同时移除前端投影，并保留投影快照用于恢复。',
          okText: '拒绝',
        },
        onClick: () => mutate('rejected', 'admin_cross_media_reject'),
      },
      {
        key: 'verify',
        label: '验证',
        icon: <CheckCircleOutlined />,
        visible: row.status !== 'verified',
        disabled: setStatusMutation.isPending,
        onClick: () => mutate('verified', 'admin_cross_media_verify'),
      },
    ]
  }

  const refetchAll = () => {
    void statsQuery.refetch()
    void listQuery.refetch()
  }

  const stats = statsQuery.data
  const counts = listQuery.data?.counts?.status ?? {}
  const selectedLinkCount = targetLinks(selectedGroups).length
  const emptyContent = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <Space direction="vertical" size={4}>
          <Typography.Text strong>
            {keyword ? '没有匹配的关联' : '当前筛选下没有风险项'}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {keyword
              ? '换个动画或漫画 ID/标题再试'
              : '切换到全部或其他风险筛选继续检查'}
          </Typography.Text>
        </Space>
      }
    />
  )

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {(statsQuery.isError || listQuery.isError) && (
        <Alert
          type="error"
          showIcon
          message={
            isRpcMissingError((listQuery.error ?? statsQuery.error) as Error)
              ? '漫画关联后台 RPC 未部署'
              : '漫画关联数据加载失败'
          }
          description={crossMediaErrorDescription(
            (listQuery.error ?? statsQuery.error) as Error,
          )}
          action={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => {
                void statsQuery.refetch()
                void listQuery.refetch()
              }}
            >
              重试
            </Button>
          }
        />
      )}

      <Row gutter={[12, 12]}>
        <Col xs={12} xl={6}>
          <Card size="small" className="cross-media-stat-card">
            <Statistic
              title="内部关联"
              value={compactCount(stats?.total_links)}
              loading={statsQuery.isLoading}
              prefix={<LinkOutlined />}
              valueStyle={NUM_STYLE}
            />
          </Card>
        </Col>
        <Col xs={12} xl={6}>
          <Card size="small" className="cross-media-stat-card">
            <Statistic
              title="前端投影"
              value={compactCount(stats?.public_projection)}
              loading={statsQuery.isLoading}
              valueStyle={NUM_STYLE}
            />
          </Card>
        </Col>
        <Col xs={12} xl={6}>
          <Card size="small" className="cross-media-stat-card">
            <Statistic
              title="多漫画动画"
              value={compactCount(stats?.multi_mapped_anime)}
              loading={statsQuery.isLoading}
              valueStyle={{
                ...NUM_STYLE,
                color: 'var(--ant-color-warning-text)',
              }}
            />
          </Card>
        </Col>
        <Col xs={12} xl={6}>
          <Card size="small" className="cross-media-stat-card">
            <Statistic
              title="孤儿投影"
              value={compactCount(stats?.orphan_projection_count)}
              loading={statsQuery.isLoading}
              valueStyle={{
                ...NUM_STYLE,
                color:
                  compactCount(stats?.orphan_projection_count) > 0
                    ? 'var(--ant-color-error-text)'
                    : undefined,
              }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        className="cross-media-list-card"
        title={
          <Space size={8} wrap className="cross-media-filter-rails">
            <Segmented
              value={risk}
              onChange={(value) => {
                setRisk(value as RiskFilter)
                setPage(1)
                setSelectedKeys([])
              }}
              options={RISK_OPTIONS}
            />
            <Segmented
              value={status}
              onChange={(value) => {
                setStatus(value as StatusFilter)
                setPage(1)
                setSelectedKeys([])
              }}
              options={STATUS_OPTIONS.map((option) => ({
                ...option,
                label: (
                  <span>
                    {option.label}
                    {option.value !== 'all' ? (
                      <span
                        style={{
                          marginLeft: 4,
                          color: 'var(--ant-color-text-tertiary)',
                          ...NUM_STYLE,
                        }}
                      >
                        {counts[option.value as LinkStatusOrOrphan] ?? 0}
                      </span>
                    ) : null}
                  </span>
                ),
              }))}
            />
          </Space>
        }
        extra={
          <Space wrap>
            <DebouncedSearchInput
              placeholder="搜索动画/漫画/ID"
              onDebouncedChange={(value) => {
                setKeyword(value)
                setPage(1)
                setSelectedKeys([])
              }}
            />
            <Tooltip
              title={
                isMobile
                  ? '清理孤儿：删除所有没有 verified 内部关联支撑的前端投影'
                  : '删除所有没有 verified 内部关联支撑的前端投影'
              }
            >
              <Button
                icon={<ClearOutlined />}
                danger
                aria-label="清理孤儿"
                disabled={cleanupMutation.isPending}
                onClick={() => void confirmCleanupOrphans()}
              >
                {isMobile ? null : '清理孤儿'}
              </Button>
            </Tooltip>
            {isMobile ? (
              <Tooltip title="刷新">
                <Button
                  icon={<ReloadOutlined />}
                  aria-label="刷新"
                  onClick={refetchAll}
                />
              </Tooltip>
            ) : null}
          </Space>
        }
      >
        {/* 桌面批量工具条；手机上改为选中后出现的吸底批量条 */}
        <Space
          className="admin-desktop-only"
          style={{
            width: '100%',
            marginBottom: 12,
            justifyContent: 'space-between',
          }}
          wrap
        >
          <Typography.Text type="secondary">
            已选 {selectedGroups.length} 个动画，{selectedLinkCount}{' '}
            条可编辑关联
          </Typography.Text>
          <Space wrap>
            <Button
              disabled={selectedLinkCount === 0 || bulkStatusMutation.isPending}
              onClick={() => confirmBulk('pending')}
            >
              批量待审
            </Button>
            <Button
              danger
              disabled={selectedLinkCount === 0 || bulkStatusMutation.isPending}
              icon={<StopOutlined />}
              onClick={() => confirmBulk('rejected')}
            >
              批量拒绝
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refetchAll}>
              刷新
            </Button>
          </Space>
        </Space>

        <div className="admin-table-wrap">
          <Table<CrossMediaGroup>
            rowKey="anime_id"
            size="small"
            loading={
              listQuery.isLoading ||
              (listQuery.isFetching && listQuery.isPlaceholderData)
            }
            columns={columns}
            dataSource={rows}
            scroll={{ x: 'max-content' }}
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: setSelectedKeys,
              getCheckboxProps: (row) => ({
                disabled: row.links.every((link) => link.status === 'orphan'),
              }),
            }}
            locale={{ emptyText: emptyContent }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              onChange: (nextPage) => {
                setPage(nextPage)
                setSelectedKeys([])
              },
            }}
          />
        </div>

        <AdminMobileList
          rows={rows}
          rowKey={(row) => row.anime_id}
          loading={
            listQuery.isLoading ||
            (listQuery.isFetching && listQuery.isPlaceholderData)
          }
          emptyContent={emptyContent}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={(nextPage) => {
            setPage(nextPage)
            setSelectedKeys([])
          }}
          renderItem={(row) => {
            const selectable = !row.links.every(
              (link) => link.status === 'orphan',
            )
            const checked = selectedKeys.includes(row.anime_id)
            return (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                aria-label={`${row.anime_title}：查看关联详情`}
                onClick={() => setDetail(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setDetail(row)
                  }
                }}
              >
                <div className="admin-mobile-record__lead">
                  <div
                    className="admin-mobile-record__select"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      aria-label={`选择 ${row.anime_title}`}
                      checked={checked}
                      disabled={!selectable}
                      onChange={(e) =>
                        setSelectedKeys((keys) =>
                          e.target.checked
                            ? [...keys, row.anime_id]
                            : keys.filter((k) => k !== row.anime_id),
                        )
                      }
                    />
                  </div>
                  <div className="cross-media-mobile-main">
                    <div className="admin-mobile-record__head">
                      <div className="admin-mobile-record__title">
                        {row.anime_title}
                      </div>
                      {riskTag(row.risk_level)}
                    </div>
                    <div className="admin-mobile-record__meta">
                      <span>#{row.anime_id}</span>
                      {row.anime_bangumi_id ? (
                        <span>bgm {row.anime_bangumi_id}</span>
                      ) : null}
                      <span>{formatCST(row.latest_at, 'MM-DD HH:mm')}</span>
                    </div>
                    <div className="admin-mobile-record__badges">
                      <Tag>总 {row.link_count}</Tag>
                      <Tag color="blue">直接 {row.direct_count}</Tag>
                      <Tag color="purple">同源 {row.same_source_count}</Tag>
                      <Tag color="green">前端 {row.public_count}</Tag>
                    </div>
                    <div className="admin-mobile-record__badges">
                      {row.verified_count > 0 ? (
                        <Tag color="success">{row.verified_count} 已验证</Tag>
                      ) : null}
                      {row.pending_count > 0 ? (
                        <Tag color="warning">{row.pending_count} 待审</Tag>
                      ) : null}
                      {row.rejected_count > 0 ? (
                        <Tag color="error">{row.rejected_count} 拒绝</Tag>
                      ) : null}
                      {row.orphan_count > 0 ? (
                        <Tag color="magenta">{row.orphan_count} 孤儿</Tag>
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            )
          }}
        />

        {selectedGroups.length > 0 ? (
          <div className="admin-mobile-bulkbar admin-mobile-only">
            <span className="admin-mobile-bulkbar__count">
              已选 {selectedGroups.length} 部 · {selectedLinkCount} 条
            </span>
            <Button
              size="small"
              disabled={selectedLinkCount === 0 || bulkStatusMutation.isPending}
              onClick={() => confirmBulk('pending')}
            >
              待审
            </Button>
            <Button
              size="small"
              danger
              disabled={selectedLinkCount === 0 || bulkStatusMutation.isPending}
              onClick={() => confirmBulk('rejected')}
            >
              拒绝
            </Button>
            <Button
              size="small"
              type="text"
              onClick={() => setSelectedKeys([])}
            >
              取消
            </Button>
          </div>
        ) : null}
      </Card>

      <Drawer
        open={detail != null}
        onClose={() => setDetail(null)}
        width={isMobile ? '100%' : 860}
        title={detail?.anime_title ?? '关联详情'}
        extra={detail ? riskTag(detail.risk_level) : null}
      >
        {detail ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions
              size="small"
              bordered
              column={2}
              layout={isMobile ? 'vertical' : 'horizontal'}
            >
              <Descriptions.Item label="动画 ID">
                {detail.anime_id}
              </Descriptions.Item>
              <Descriptions.Item label="Bangumi">
                {detail.anime_bangumi_id ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="关联数">
                {detail.link_count}
              </Descriptions.Item>
              <Descriptions.Item label="前端显示">
                {detail.public_count}
              </Descriptions.Item>
            </Descriptions>

            {isMobile ? (
              /* 手机：关联列表纵向卡片，替代 6 列横滚表格 */
              <AdminMobileList<CrossMediaLink>
                rows={detail.links}
                rowKey={linkKey}
                renderItem={(link) => (
                  <article className="admin-mobile-record cross-media-link-card">
                    <div className="admin-mobile-record__head">
                      <div className="admin-mobile-record__title">
                        {link.comic_title ?? `漫画 #${link.comic_id}`}
                      </div>
                      {statusTag(link.status)}
                    </div>
                    <div className="admin-mobile-record__meta">
                      <span>#{link.comic_id}</span>
                      {link.comic_slug ? (
                        <span>slug {link.comic_slug}</span>
                      ) : null}
                      <span>置信 {confidenceText(link.confidence)}</span>
                    </div>
                    <div className="admin-mobile-record__badges">
                      <Tag
                        color={
                          link.relation_type === 'direct_adaptation'
                            ? 'blue'
                            : 'purple'
                        }
                      >
                        {relationLabel(link.relation_type)}
                      </Tag>
                      {link.source_work_bangumi_id ? (
                        <Tag>source {link.source_work_bangumi_id}</Tag>
                      ) : null}
                      <Tag>{link.match_method}</Tag>
                      {link.in_public_projection ? (
                        <Badge status="success" text="前端显示" />
                      ) : null}
                    </div>
                    {link.status === 'orphan' ? (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        孤儿投影：用列表页「清理孤儿」处理
                      </Typography.Text>
                    ) : (
                      <AdminRecordActions actions={linkActions(link)} />
                    )}
                  </article>
                )}
              />
            ) : (
              <Table<CrossMediaLink>
                rowKey={linkKey}
                size="small"
                columns={linkColumns}
                dataSource={detail.links}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            )}
          </Space>
        ) : null}
      </Drawer>
    </Space>
  )
}
